import { useContext, useEffect, useState } from "react";
import styles from "../Volume.module.css";
import {
    Banner,
    Button,
    Card,
    Collapsible,
    Divider,
    FormLayout,
    Icon,
    Label,
    LegacyCard,
    Popover,
    ActionList,
    Tooltip,
    Badge,
    Thumbnail,
} from "@shopify/polaris";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
    BundleTypes,
    getDiscountOptions,
    getDiscountOptionsWIthoutFixed,
    getProductsDynamicallyAndReturnNewProductsArray,
} from "../../constants";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import {
    DragHandleIcon,
    DeleteIcon,
    PlusIcon,
    ProductIcon,
    OrderIcon,
    AlertTriangleIcon,
} from "@shopify/polaris-icons";
import { VolumeBundleContext, VolumeBundleContextType } from "../VolumeDiscount";
import type {
    DefaultVariantType,
    ErrorInterface,
    OfferInterface,
    ProductObj,
    ProductSourceType,
    VariantsObj,
} from "../../Types";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import RHFRenderSelectedProduct from "../../../../components/RHFRenderSelectedProduct/RHFRenderSelectedProduct";
import { buildResourcePickerQuery, CurrencyTypes, extractNumericIds } from "../../../constants";
import BundleInfo from "./BundleInfo";
import VolumeDesignTemplateQuickSelect from "./VolumeDesignTemplateQuickSelect";
import { sanitizeValue } from "../../../../utilities/functions";
import useBundleMixpanelHook from "../../../../hooks/useBundleMixpanelHook";
import VolumeFreeGiftForm from "./VolumeFreeGiftForm";
import UpsellVolume from "./UpsellVolume";
import { filterFreeGifts } from "../../../../components/FreeGift/FreeGiftConstants";
import TextFields from "./TextFields";
import RHFSwatchesSelectorWrapper from "../../../../components/RHFSwatchesSelectorWrapper/RHFSwatchesSelectorWrapper";
import RHFSavingSummary from "../../../../components/RHFSavingSummary/RHFSavingSummary";
import { DiscountCombinations } from "./Discount";
import RHFRenderSelectedCollections from "../../../../components/RHFRenderSelectedCollections/RHFRenderSelectedCollections";
import DefaultVariantSelector from "../../../../components/DefaultVariantSelector/DefaultVariantSelector";
import { PiBracketsCurlyBold } from "react-icons/pi";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import { FormCheckbox, FormRadioButton, FormTextField, FormSelect } from "../../../../components/FormFields";
import IconUploader, { IconOption } from "../../../../components/IconUploader/IconUploader";
import ImageSettingsModal from "../../../../components/ImageSettingsModal/ImageSettingsModal";
import { VolumePreview } from "../VolumeBundle/VolumeBundle";
import CustomTooltipAdmin from "../../../../components/CustomTooltipAdmin/CustomTooltipAdmin";

const getCurrencySymbol = (currencyCode: string) => {
    const currency = CurrencyTypes.find((c) => c.country_code === currencyCode);
    return currency ? currency.symbol : "";
};

const getDiscountSuffix = (discountType: string, currency: string) => {
    if (!discountType) return null;
    if (discountType === "percentage") return "%";
    if (discountType === "fixed" || discountType === "flat") return getCurrencySymbol(currency);
    return null;
};

const VolumeContentForm = ({ showBundlerLevel = false }: { showBundlerLevel?: boolean }) => {
    const [expandProductSetup, setExpandProductSetup] = useState(true);
    const [expandTemplate, setExpandTemplate] = useState(true);
    const context = useContext(VolumeBundleContext);
    const { trackFormSections } = useBundleMixpanelHook();
    const appConfigContext = useContext(AppConfigContext);
    const { currency } = appConfigContext;

    const {
        control,
        setValue,
        getValues,
        formState: { errors: formErrors },
    } = useFormContext();

    const { fields: offerFields, append, remove, move } = useFieldArray({ control, name: "volume_discounts" });

    const volumeProductSelection = useWatch({ control, name: "volume_product_selection" });
    const bundleProductLevel = useWatch({ control, name: "bundle_product_level" });
    const products = useWatch({ control, name: "products" }) ?? [];
    const collections = useWatch({ control, name: "collections" }) ?? [];
    const defaultOffer = useWatch({ control, name: "default_offer" });
    const defaultVariantTypes = useWatch({ control, name: "defaultVariantTypes" }) ?? [];
    const watchedOffers: OfferInterface[] = useWatch({ control, name: "volume_discounts" }) ?? [];
    const allOffers: OfferInterface[] =
        watchedOffers.length >= offerFields.length ? watchedOffers : (offerFields as unknown as OfferInterface[]);

    const {
        handleNext = () => {},
        isMobile,
        SwatchesBundleConfigTouchedRef,
        openDefaultVariantSelector,
        setOpenDefaultVariantSelector,
        activeQuantity,
        setActiveQuantity,
        activeProduct,
        setActiveProduct,
        selectedTab,
        setSelectedTab,
    } = context as VolumeBundleContextType;

    const [errors, setErrors] = useState<ErrorInterface[]>([]);
    const [popoverActive, setPopoverActive] = useState(false);

    const getOfferTitle = (number = 0) => {
        const titles: Record<number, string> = {
            1: "Buy One",
            2: "Buy Two",
            3: "Buy Three",
            4: "Buy Four",
            5: "Buy Five",
            6: "Buy Six",
            7: "Buy Seven",
        };
        return titles[number] ?? "Buy more";
    };

    const handleAddOffer = (type: "quantity_break" | "product_bundle" = "quantity_break") => {
        if (allOffers.length < 7) {
            const lastOffer = allOffers[allOffers.length - 1] ?? {};
            const newOffer: OfferInterface = {
                discount_type: (lastOffer as OfferInterface).discount_type ?? "percentage",
                quantity: allOffers.length + 1,
                offer_title: getOfferTitle(allOffers.length + 1),
                discount_value:
                    type === "product_bundle" ? 10 : ((lastOffer as OfferInterface).discount_value ?? 0) + 5,
                discount_label: "",
                discount_label_selected: false,
                discount_label_type: "none",
                discount_label_custom_img: "",
                discount_label_custom_img_size: 40,
                discount_label_custom_img_radius: 0,
                discount_label_custom_img_fit: "contain",
                tag: "",
                tag_selected: false,
                allow_free_gifts: false,
                free_gifts: [],
                free_gift_text: "+Free Gift",
                allow_upsell_products: false,
                upsell_products: [],
                allow_img: false,
                img_url: "",
                img_size: 50,
                img_radius: 8,
                img_fit: "cover",
                allow_extra_items: false,
                show_quantity_selector: false,
                free_gift_template: "modern",
                free_gift_show_price: true,
                offer_type: type,
                bundle_products: [],
                bundle_layout: "vertical",
            };

            append(newOffer);
            setSelectedTab(allOffers.length);
        }
        setPopoverActive(false);
    };

    const handleInputChange = (
        index: number,
        field: keyof OfferInterface,
        value: string | number | boolean | ProductObj[],
    ) => {
        const updatedErrors = [...errors];
        if (!updatedErrors[index]) updatedErrors[index] = {} as ErrorInterface;

        if (typeof value === "number" && isNaN(value)) {
            if (field === "quantity") updatedErrors[index].quantity = "Quantity must be a valid number";
            else if (field === "discount_value")
                updatedErrors[index].discount_value = "Discount value must be a valid number";
        } else {
            if (field === "quantity" && typeof value === "number") {
                if (value > 2000) {
                    updatedErrors[index].quantity = "Product quantity must be between 1 and 2000";
                    value = 2000;
                } else if (value < 1) {
                    updatedErrors[index].quantity = "Product quantity must be between 1 and 2000";
                    value = 1;
                } else updatedErrors[index].quantity = "";
            }
            if (field === "discount_value" && typeof value === "number") {
                if (value < 0) {
                    updatedErrors[index].discount_value = "Discount value must be positive";
                    value = 1;
                } else updatedErrors[index].discount_value = "";
            }
            if (field === "offer_title" && typeof value === "string") {
                updatedErrors[index].offer_title = value.trim() === "" ? "Offer title is required" : "";
            }
            if (field === "img_url" && typeof value === "string") {
                updatedErrors[index].img_url = value.trim() === "" ? "Offer Image is required" : "";
            }
        }

        setErrors(updatedErrors);
        setValue(`volume_discounts.${index}.${field}` as any, value, { shouldDirty: true });
    };

    const handleDeleteOffer = (index: number) => {
        if (allOffers.length === 1) {
            shopify.toast.show("At least one offer is necessary", { isError: true });
            return;
        }
        if (defaultOffer === selectedTab) {
            const newDefault = Math.max(0, defaultOffer - 1);
            setValue("default_offer", newDefault, { shouldDirty: true });
        }
        remove(index);
        if (selectedTab === index) {
            setSelectedTab(Math.max(0, selectedTab - 1));
        }
    };

    const handleDragEnd = (result: any) => {
        if (!result.destination) return;
        const src = result.source.index;
        const dst = result.destination.index;
        if (defaultOffer === src) {
            setValue("default_offer", dst, { shouldDirty: true });
        }
        move(src, dst);
        const reorderedErrors = Array.from(errors);
        const [movedError] = reorderedErrors.splice(src, 1);
        reorderedErrors.splice(dst, 0, movedError);
        setErrors(reorderedErrors);
        setSelectedTab(dst);
    };

    const handleMultipleInputChanges = (index: number, updates: Partial<OfferInterface>) => {
        Object.entries(updates).forEach(([field, value]) => {
            setValue(`volume_discounts.${index}.${field}` as any, value, { shouldDirty: true });
        });
    };

    // Clamp percentage discount_value to 100
    useEffect(() => {
        const currentDiscountType = allOffers[selectedTab]?.discount_type;
        const currentDiscountValue = allOffers[selectedTab]?.discount_value;
        if (currentDiscountType === "percentage" && currentDiscountValue > 100) {
            handleInputChange(selectedTab, "discount_value", 100);
        }
    }, [allOffers[selectedTab]?.discount_type, allOffers[selectedTab]?.discount_value]);

    // Filter free gifts when products change
    useEffect(() => {
        if (!products?.length) return;
        let isChanged = false;
        const tempOffers = allOffers.map((offer: OfferInterface) => {
            if (!offer?.free_gifts?.length) return offer;
            try {
                const status = filterFreeGifts(offer.free_gifts, [...products]);
                if (status?.isChanged) isChanged = true;
                return { ...offer, free_gifts: status?.isChanged ? status.arr : offer.free_gifts };
            } catch {
                return offer;
            }
        });
        if (isChanged) {
            setValue("volume_discounts", tempOffers, { shouldDirty: true });
        }
    }, [products]);

    // Remove excluded products from defaultVariantTypes
    useEffect(() => {
        if (volumeProductSelection !== "exclude" || !products?.length) return;
        const excludedIds = new Set(products.map((p: ProductObj) => p.id));
        const filteredTypes = defaultVariantTypes.filter((dv: DefaultVariantType) => !excludedIds.has(dv.productID));
        setValue("defaultVariantTypes", filteredTypes, { shouldDirty: true });
    }, [products, volumeProductSelection]);

    const currentOffer = allOffers[selectedTab];

    return (
        <div className={styles.formWrapper}>
            <div style={{ marginBottom: "4px" }}>
                <Divider borderColor="border" />
            </div>
            <div className={styles.collapsibleFormBox}>
                <BundleInfo bundleType="volume" />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <div
                    className={styles.sectionTitleWrapper}
                    onClick={() =>
                        setExpandTemplate((ps) => {
                            trackFormSections({
                                cta: "Form Section",
                                ctaType: `Template ${ps ? "Collapsed" : "Expanded"}`,
                                bundleType: "volume",
                            });
                            return !ps;
                        })
                    }
                >
                    <div>Template</div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: expandTemplate ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                    >
                        <MdOutlineArrowBackIosNew size={14} />
                    </div>
                </div>

                <Collapsible
                    open={expandTemplate}
                    id="template-collapsible"
                    transition={{ duration: "500ms", timingFunction: "ease-in-out" }}
                    expandOnPrint
                >
                    <VolumeDesignTemplateQuickSelect />
                </Collapsible>

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <div
                    className={styles.sectionTitleWrapper}
                    onClick={() =>
                        setExpandProductSetup((ps) => {
                            trackFormSections({
                                cta: "Form Section",
                                ctaType: `Product & Offer Setup ${ps ? "Collapsed" : "Expanded"}`,
                                bundleType: "volume",
                            });
                            return !ps;
                        })
                    }
                >
                    <div>Product and Offer Setup</div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: expandProductSetup ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                    >
                        <MdOutlineArrowBackIosNew size={14} />
                    </div>
                </div>

                <Collapsible
                    open={expandProductSetup}
                    id="basic-collapsible"
                    transition={{ duration: "500ms", timingFunction: "ease-in-out" }}
                    expandOnPrint
                >
                    <FormLayout>
                        {showBundlerLevel && (
                            <div className={`${styles.formCol}`}>
                                <div className={styles.customLabelWrapper} style={{ marginBottom: "5px" }}>
                                    <Label id="bundle_product_level">
                                        <span>Bundle Level</span>
                                    </Label>
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", marginTop: "5px" }}>
                                    <>
                                        <FormRadioButton
                                            label="Product Level: Variants in Dropdown"
                                            id="product"
                                            helpText="Selected variants will appear in a dropdown under each product in the bundle."
                                            value="product"
                                            name="bundle_product_level"
                                            onChangeHook={() => {
                                                setValue("products", [], { shouldDirty: true });
                                            }}
                                        />
                                        {bundleProductLevel === "variant" && (
                                            <FormRadioButton
                                                label="Variant Level: Variants as Standalone"
                                                id="variant"
                                                helpText="Selected variants will be displayed as individual, standalone products in the bundle."
                                                value="variant"
                                                name="bundle_product_level"
                                                onChangeHook={() => {
                                                    setValue("products", [], { shouldDirty: true });
                                                }}
                                            />
                                        )}
                                    </>
                                </div>
                            </div>
                        )}

                        <div className={`${styles.formCol}`}>
                            <div style={{ display: "flex", flexDirection: "column", marginTop: "-5px" }}>
                                <>
                                    <FormRadioButton
                                        label="All Products"
                                        id="all"
                                        value="all"
                                        name="volume_product_selection"
                                        onChangeHook={() => {
                                            setValue("products", [], { shouldDirty: true });
                                        }}
                                    />
                                    <FormRadioButton
                                        label="Specific Products"
                                        id="specific"
                                        value="specific"
                                        name="volume_product_selection"
                                        onChangeHook={() => {
                                            setValue("products", [], { shouldDirty: true });
                                        }}
                                    />
                                    <FormRadioButton
                                        label="Collection"
                                        id="collection"
                                        value="collection"
                                        name="volume_product_selection"
                                        onChangeHook={() => {
                                            setValue("products", [], { shouldDirty: true });
                                        }}
                                    />
                                    <FormRadioButton
                                        label="Exclude Products"
                                        id="exclude"
                                        value="exclude"
                                        name="volume_product_selection"
                                        onChangeHook={() => {
                                            setValue("products", [], { shouldDirty: true });
                                        }}
                                    />
                                </>
                            </div>
                        </div>

                        {volumeProductSelection !== "all" && (
                            <>
                                {volumeProductSelection === "collection" ? (
                                    <RHFRenderSelectedCollections updateKey="collections" multiple={false} />
                                ) : (
                                    <RHFRenderSelectedProduct
                                        customTitle={volumeProductSelection === "exclude" ? "Excluded" : "Included"}
                                        bundleLevel={bundleProductLevel ?? "product"}
                                        updateKey="products"
                                        bundleType={BundleTypes.VOLUME_DISCOUNT}
                                        multiple={true}
                                    />
                                )}
                            </>
                        )}

                        <div className={styles.customLabelWrapper} style={{ marginTop: "5px", marginBottom: "5px" }}>
                            <Label id="discount_combinations">Subscriptions</Label>
                        </div>

                        <div style={{ marginBottom: "10px" }}>
                            <FormCheckbox
                                label="Enable Subscription Integration"
                                name="enable_subscription_integration"
                            />
                            <LegacyCard>
                                <Banner>
                                    <p>
                                        If you experience any difficulty with subscriptions, please contact support.
                                        Some subscription apps may not be fully supported.
                                    </p>
                                </Banner>
                            </LegacyCard>
                        </div>

                        {/* Draggable Offers */}
                        <div className={styles.customLabelWrapper} style={{ marginTop: "5px", marginBottom: "5px" }}>
                            <Label id="discount_combinations">Discounts</Label>
                        </div>

                        <div className={styles.container}>
                            <div className={styles.offerTabContainer}>
                                <style>{`.volume-offer-tab-warning-icon svg path { fill: #B98900 !important; }`}</style>
                                {/* @ts-expect-error hack */}
                                <DragDropContext onDragEnd={handleDragEnd}>
                                    {/* @ts-expect-error hack */}
                                    <Droppable droppableId="droppable-tabs" direction="horizontal">
                                        {(provided) => (
                                            <div
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                className={styles.offerTabDroppableContainer}
                                            >
                                                {offerFields.map((_, index) => {
                                                    const hasOfferError = !!(formErrors as any)?.volume_discounts?.[
                                                        index
                                                    ];
                                                    const button = (
                                                        <Button
                                                            pressed={selectedTab === index}
                                                            onClick={() => setSelectedTab(index)}
                                                            icon={
                                                                hasOfferError ? (
                                                                    <span className="volume-offer-tab-warning-icon">
                                                                        <Icon source={AlertTriangleIcon} />
                                                                    </span>
                                                                ) : undefined
                                                            }
                                                        >
                                                            {`Offer ${index + 1}`}
                                                        </Button>
                                                    );
                                                    return (
                                                        // @ts-expect-error hack
                                                        <Draggable
                                                            key={index}
                                                            draggableId={index.toString()}
                                                            index={index}
                                                        >
                                                            {(provided) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    style={{
                                                                        ...provided.draggableProps.style,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "2px",
                                                                    }}
                                                                >
                                                                    <div
                                                                        {...provided.dragHandleProps}
                                                                        style={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            cursor: "grab",
                                                                        }}
                                                                    >
                                                                        <Icon source={DragHandleIcon} />
                                                                    </div>
                                                                    <CustomTooltipAdmin
                                                                        content={
                                                                            hasOfferError
                                                                                ? "This offer has an error"
                                                                                : undefined
                                                                        }
                                                                    >
                                                                        {button}
                                                                    </CustomTooltipAdmin>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                                {provided.placeholder as any}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>

                                {offerFields.length < 7 && (
                                    <Popover
                                        active={popoverActive}
                                        activator={
                                            <Button
                                                onClick={() => setPopoverActive(!popoverActive)}
                                                variant="secondary"
                                                icon={PlusIcon}
                                                disclosure
                                            >
                                                Add Offer
                                            </Button>
                                        }
                                        onClose={() => setPopoverActive(false)}
                                    >
                                        <ActionList
                                            items={[
                                                {
                                                    content: "Quantity Breaks",
                                                    helpText: "Volume discount tiers",
                                                    icon: OrderIcon,
                                                    onAction: () => handleAddOffer("quantity_break"),
                                                },
                                                {
                                                    content: "Product Bundle Offer",
                                                    helpText: "Group products together",
                                                    icon: ProductIcon,
                                                    onAction: () => handleAddOffer("product_bundle"),
                                                },
                                            ]}
                                        />
                                    </Popover>
                                )}
                            </div>

                            {currentOffer && (
                                <Card>
                                    <div style={{ position: "absolute", top: "5px", right: "5px" }}>
                                        <Button
                                            tone="critical"
                                            variant="secondary"
                                            onClick={() => handleDeleteOffer(selectedTab)}
                                            icon={DeleteIcon}
                                        />
                                    </div>
                                    <div className={styles.tabContent}>
                                        {currentOffer?.offer_type === "product_bundle" ? (
                                            <ProductBundleSetup
                                                selectedTab={selectedTab}
                                                handleInputChange={handleInputChange}
                                                allOffers={allOffers}
                                                errors={errors}
                                                currency={currency}
                                                defaultOffer={defaultOffer}
                                                products={products}
                                                handleMultipleInputChanges={handleMultipleInputChanges}
                                                activeProduct={activeProduct}
                                                volume_product_selection={volumeProductSelection}
                                                isMobile={isMobile}
                                                setValue={setValue}
                                            />
                                        ) : (
                                            <QuantityBreakSetup
                                                selectedTab={selectedTab}
                                                handleInputChange={handleInputChange}
                                                allOffers={allOffers}
                                                errors={errors}
                                                currency={currency}
                                                defaultOffer={defaultOffer}
                                                products={products}
                                                handleMultipleInputChanges={handleMultipleInputChanges}
                                                setValue={setValue}
                                            />
                                        )}
                                    </div>
                                </Card>
                            )}
                        </div>

                        <DiscountCombinations bundleType={BundleTypes.VOLUME_DISCOUNT} />
                    </FormLayout>
                </Collapsible>

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <RHFSavingSummary />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <RHFSwatchesSelectorWrapper
                    bundleType={BundleTypes.VOLUME_DISCOUNT}
                    SwatchesBundleConfigTouchedRef={SwatchesBundleConfigTouchedRef}
                    setOpenDefaultVariantSelector={setOpenDefaultVariantSelector}
                    productSource={
                        {
                            type: volumeProductSelection || "specific",
                            value: (volumeProductSelection === "collection" ? collections : products) || [],
                        } as ProductSourceType
                    }
                />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <TextFields bundleType={BundleTypes.VOLUME_DISCOUNT} />
            </div>

            <DefaultVariantSelector
                openDefaultVariantSelector={openDefaultVariantSelector}
                setOpenDefaultVariantSelector={setOpenDefaultVariantSelector}
                bundleConfig={getValues() as any}
                activeQuantity={activeQuantity}
                setActiveQuantity={setActiveQuantity}
                activeProduct={activeProduct || products?.[0] || null}
                setActiveProduct={setActiveProduct}
                defaultVariantTypes={{
                    value: defaultVariantTypes,
                    onChange: (value) => setValue("defaultVariantTypes", value, { shouldDirty: true }),
                }}
                allowVariantSelection={false}
                resourcePickerQuery={buildResourcePickerQuery({
                    products: extractNumericIds(products),
                    collections: extractNumericIds(collections),
                    filters: [volumeProductSelection],
                })}
            />

            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>

            <div className={styles.bottomRowCta}>
                <span></span>
                <Button onClick={() => handleNext(1, "Design")}>Next</Button>
            </div>
        </div>
    );
};

export default VolumeContentForm;

// ---------------------------------------------------------------------------
// Small Internal Field Components (RHF-native)
// ---------------------------------------------------------------------------

export const OfferTitleField = ({ offer, index, handleInputChange }: any) => (
    <div className={styles.inputWrapper}>
        <div>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "4px",
                    marginBottom: "6px",
                    cursor: "default",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>Offer Title</div>
                <div>
                    <Tooltip width="wide" content={"Use {discount} to show the actual discount value"}>
                        <PiBracketsCurlyBold />
                    </Tooltip>
                </div>
            </div>
            <FormTextField
                name={`volume_discounts.${index}.offer_title`}
                label=""
                labelHidden
                value={offer?.offer_title ?? ""}
                maxLength={50}
                showCharacterCount
                onChange={(value: string) => handleInputChange(index, "offer_title", value)}
                autoComplete="off"
            />
        </div>
    </div>
);

const VariableTextField = ({
    name,
    label,
    value,
    onChange,
    maxLength,
    showCharacterCount,
    autoComplete,
    disabled,
}: any) => {
    const [popoverActive, setPopoverActive] = useState(false);
    const togglePopoverActive = () => setPopoverActive((active) => !active);
    const handleVariableInsert = (variable: string) => {
        onChange((value || "") + variable);
        setPopoverActive(false);
    };

    return (
        <div className={styles.inputWrapper}>
            <div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "4px",
                        marginBottom: "6px",
                        cursor: "default",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>{label}</div>
                    <div>
                        <Popover
                            active={popoverActive}
                            activator={
                                <div onClick={togglePopoverActive} style={{ cursor: "pointer", display: "flex" }}>
                                    <Tooltip content="Add variable">
                                        <PiBracketsCurlyBold />
                                    </Tooltip>
                                </div>
                            }
                            onClose={togglePopoverActive}
                        >
                            <ActionList
                                items={[
                                    {
                                        content: "Saved percentage",
                                        onAction: () => handleVariableInsert("{saved_percentage}"),
                                    },
                                    { content: "Saved $ total", onAction: () => handleVariableInsert("{saved_total}") },
                                    {
                                        content: "Saved $ per item",
                                        onAction: () => handleVariableInsert("{saved_per_item}"),
                                    },
                                ]}
                            />
                        </Popover>
                    </div>
                </div>
                <FormTextField
                    name={name}
                    label=""
                    labelHidden
                    value={value ?? ""}
                    maxLength={maxLength}
                    showCharacterCount={showCharacterCount}
                    onChange={(val: string) => onChange(val)}
                    autoComplete={autoComplete}
                    disabled={disabled}
                />
            </div>
        </div>
    );
};

export const OfferTagField = ({ offer, index, handleInputChange }: any) => (
    <VariableTextField
        name={`volume_discounts.${index}.tag`}
        label="Tag"
        value={offer.tag}
        maxLength={50}
        showCharacterCount
        onChange={(value: string) => handleInputChange(index, "tag", value)}
        autoComplete="off"
    />
);

export const OfferLabelField = ({ offer, index, handleInputChange }: any) => (
    <VariableTextField
        name={`volume_discounts.${index}.discount_label`}
        label="Discount label"
        value={offer.discount_label}
        maxLength={50}
        showCharacterCount
        onChange={(value: string) => handleInputChange(index, "discount_label", value)}
        autoComplete="off"
        disabled={offer.discount_label_type === "custom"}
    />
);

export const SelectedByDefaultField = ({ index, defaultOffer, setValue }: any) => (
    <div className={styles.inputWrapper} style={{ alignSelf: "end" }}>
        <FormCheckbox
            label="Selected by default"
            name="default_offer"
            checked={defaultOffer === index}
            onChange={(value: boolean) => {
                setValue("default_offer", value ? index : -1, { shouldDirty: true });
            }}
        />
    </div>
);

// In "all"/"collection"/"exclude" selection modes, `products` is intentionally kept empty on
// the saved form (see VolumeDiscount.tsx) — this modal's live preview needs a real product to
// render though, so it borrows the same representative product VolumeDiscount.tsx's own main
// preview already fetched into VolumeBundleContext, instead of duplicating that fetch here.
const getPreviewProducts = (formValues: any, activeProduct: ProductObj | null): ProductObj[] =>
    formValues.volume_product_selection !== "specific" && activeProduct ? [activeProduct] : formValues.products || [];

export const DiscountLabelBadgePicker = ({ index }: any) => {
    const { control, getValues } = useFormContext();
    const appConfigContext = useContext(AppConfigContext);
    const { activeProduct } = useContext(VolumeBundleContext) as VolumeBundleContextType;
    const [imageModalOpen, setImageModalOpen] = useState(false);
    useWatch({ control });

    const options: IconOption[] = [
        { label: "Text label", value: "none" },
        { label: "Ribbon", value: "ribbon" },
        { label: "Ticket", value: "ticket" },
        {
            label: "Tag",
            value: "tag",
            tooltip: "Best suited with the Vertical Modern template and when the Tag text field is left empty",
        },
        { label: "Skull", value: "skull" },
        { label: "Flag", value: "flag" },
        { label: "Custom", value: "custom" },
    ];

    const renderPreview = () => {
        const formValues = getValues();
        const previewBundleConfig = {
            ...formValues,
            default_offer: index,
            products: getPreviewProducts(formValues, activeProduct),
        };
        return <VolumePreview pId={0} appConfigContext={appConfigContext} bundleConfig={previewBundleConfig as any} />;
    };

    return (
        <div className={styles.inputWrapper}>
            <IconUploader
                iconTypeName={`volume_discounts.${index}.discount_label_type`}
                iconUrlName={`volume_discounts.${index}.discount_label_custom_img`}
                sizeFieldName=""
                label="Discount label style"
                options={options}
                showPreviewInDropdown={false}
                onCustomImageClick={() => setImageModalOpen(true)}
            />
            <ImageSettingsModal
                open={imageModalOpen}
                onClose={() => setImageModalOpen(false)}
                title="Add image"
                urlFieldName={`volume_discounts.${index}.discount_label_custom_img`}
                sizeFieldName={`volume_discounts.${index}.discount_label_custom_img_size`}
                radiusFieldName={`volume_discounts.${index}.discount_label_custom_img_radius`}
                fitFieldName={`volume_discounts.${index}.discount_label_custom_img_fit`}
                minSize={20}
                maxSize={100}
                renderPreview={renderPreview}
            />
        </div>
    );
};

export const OfferImageField = ({ index }: { index: number }) => {
    const { control, setValue, getValues } = useFormContext();
    const appConfigContext = useContext(AppConfigContext);
    const { activeProduct } = useContext(VolumeBundleContext) as VolumeBundleContextType;
    const [modalOpen, setModalOpen] = useState(false);
    const imgUrl = useWatch({ control, name: `volume_discounts.${index}.img_url` });
    useWatch({ control });

    const handleClose = () => {
        setModalOpen(false);
        setValue(`volume_discounts.${index}.allow_img`, !!imgUrl, { shouldDirty: true });
    };

    const renderPreview = () => {
        const formValues = getValues();
        const previewBundleConfig = {
            ...formValues,
            default_offer: index,
            products: getPreviewProducts(formValues, activeProduct),
            volume_discounts: (formValues.volume_discounts || []).map((o: OfferInterface, i: number) =>
                i === index ? { ...o, allow_img: true } : o,
            ),
        };
        return <VolumePreview pId={0} appConfigContext={appConfigContext} bundleConfig={previewBundleConfig as any} />;
    };

    return (
        <>
            <div style={{ flex: 1 }}>
                <Button onClick={() => setModalOpen(true)} fullWidth pressed={!!imgUrl}>
                    {imgUrl ? "Edit Image" : "Add Image"}
                </Button>
            </div>
            <ImageSettingsModal
                open={modalOpen}
                onClose={handleClose}
                title="Add image"
                urlFieldName={`volume_discounts.${index}.img_url`}
                sizeFieldName={`volume_discounts.${index}.img_size`}
                radiusFieldName={`volume_discounts.${index}.img_radius`}
                fitFieldName={`volume_discounts.${index}.img_fit`}
                renderPreview={renderPreview}
            />
        </>
    );
};

export const OfferActionButtons = ({ offer, index, handleInputChange }: any) => (
    <div style={{ display: "flex", gap: "10px", marginTop: "10px", marginBottom: "10px" }}>
        {offer?.offer_type !== "product_bundle" && (
            <div style={{ flex: 1 }}>
                <Button
                    onClick={() => handleInputChange(index, "allow_free_gifts", true)}
                    fullWidth
                    pressed={!!offer.allow_free_gifts}
                >
                    {offer.allow_free_gifts ? "Edit Free Gift" : "Add Free Gift"}
                </Button>
            </div>
        )}
        {offer?.offer_type !== "product_bundle" && (
            <div style={{ flex: 1 }}>
                <Button
                    onClick={() => handleInputChange(index, "allow_upsell_products", true)}
                    fullWidth
                    pressed={!!offer.allow_upsell_products}
                >
                    {offer.allow_upsell_products ? "Edit Upsell" : "Add Upsell"}
                </Button>
            </div>
        )}
        <OfferImageField index={index} />
    </div>
);

export const OfferEditors = ({
    offer,
    index,
    handleInputChange,
    products,
    handleMultipleInputChanges,
    currency,
}: any) => (
    <>
        {!!offer.allow_free_gifts && (
            <VolumeFreeGiftForm
                handleInputChange={handleInputChange}
                selectedTab={index}
                allow_free_gifts={offer.allow_free_gifts}
                free_gifts={offer.free_gifts}
                free_gift_text={offer.free_gift_text}
                excludeProducts={[...products]}
                free_gift_template={offer.free_gift_template}
                free_gift_show_price={offer.free_gift_show_price}
                handleMultipleInputChanges={handleMultipleInputChanges}
                offerError=""
            />
        )}
        {!!offer.allow_upsell_products && (
            <UpsellVolume
                handleInputChange={handleInputChange}
                selectedTab={index}
                allow_upsell_products={offer.allow_upsell_products}
                upsell_products={offer.upsell_products || []}
                excludeProducts={[...products]}
                currency={currency}
                offerError=""
                submit={() => {}}
            />
        )}
    </>
);

export const QuantityBreakSetup = ({
    selectedTab,
    handleInputChange,
    allOffers,
    errors,
    currency,
    defaultOffer,
    products,
    handleMultipleInputChanges,
    setValue,
}: any) => {
    const offer = allOffers[selectedTab];
    if (!offer) return null;
    return (
        <>
            <div className={styles.inputGroup}>
                <div className={styles.inputWrapper}>
                    <FormSelect
                        name={`volume_discounts.${selectedTab}.discount_type`}
                        label="Discount Type"
                        placeholder="Select discount type"
                        options={getDiscountOptions(getCurrencySymbol(currency))}
                        onChange={(value) => handleInputChange(selectedTab, "discount_type", value)}
                        value={offer.discount_type}
                    />
                </div>
                <div
                    className={styles.inputWrapper}
                    style={{ visibility: getDiscountSuffix(offer.discount_type, currency) ? "visible" : "hidden" }}
                >
                    <FormTextField
                        name={`volume_discounts.${selectedTab}.discount_value`}
                        type="number"
                        label="Value"
                        placeholder="value"
                        min={1}
                        value={offer.discount_value?.toString() ?? ""}
                        suffix={getDiscountSuffix(offer.discount_type, currency) ?? undefined}
                        onBlur={() =>
                            handleInputChange(selectedTab, "discount_value", offer.discount_value?.toString() || "0")
                        }
                        autoComplete="off"
                        onChange={(value) => {
                            const regex = /^(\d+(\.\d{0,2})?)?$/;
                            if (regex.test(value)) {
                                if (offer.discount_type === "percentage" && +value > 100) value = "100";
                                handleInputChange(selectedTab, "discount_value", value);
                            }
                        }}
                    />
                </div>
                <div className={styles.inputWrapper}>
                    <FormTextField
                        name={`volume_discounts.${selectedTab}.quantity`}
                        label="Quantity"
                        value={sanitizeValue(offer.quantity)}
                        onChange={(value) => {
                            const sanitizedValue = sanitizeValue(parseFloat(value));
                            const finalValue = Math.max(0, Math.min(+sanitizedValue, 2000));
                            handleInputChange(selectedTab, "quantity", finalValue);
                        }}
                        min={0}
                        max={2000}
                        type="number"
                        autoComplete="off"
                    />
                </div>

                <OfferTitleField offer={offer} index={selectedTab} handleInputChange={handleInputChange} />
                <OfferTagField offer={offer} index={selectedTab} handleInputChange={handleInputChange} />
                <OfferLabelField offer={offer} index={selectedTab} handleInputChange={handleInputChange} />
                <DiscountLabelBadgePicker offer={offer} index={selectedTab} handleInputChange={handleInputChange} />
            </div>
            <SelectedByDefaultField index={selectedTab} defaultOffer={defaultOffer} setValue={setValue} />
            <div className={styles.inputWrapper}>
                <FormCheckbox
                    label="Allow Users to Buy Extra Items with this Discount Offer"
                    name={`volume_discounts.${selectedTab}.allow_extra_items`}
                    checked={offer.allow_extra_items}
                    onChange={(value: boolean) => handleInputChange(selectedTab, "allow_extra_items", value)}
                />
            </div>
            <div className={styles.inputWrapper}>
                <FormCheckbox
                    label="Show Quantity Selector"
                    name={`volume_discounts.${selectedTab}.show_quantity_selector`}
                    checked={offer.show_quantity_selector}
                    onChange={(value: boolean) => handleInputChange(selectedTab, "show_quantity_selector", value)}
                />
            </div>
            <OfferActionButtons offer={offer} index={selectedTab} handleInputChange={handleInputChange} />
            <OfferEditors
                offer={offer}
                index={selectedTab}
                handleInputChange={handleInputChange}
                errors={errors}
                products={products}
                handleMultipleInputChanges={handleMultipleInputChanges}
                currency={currency}
            />
        </>
    );
};

export const ProductBundleSetup = ({
    selectedTab,
    handleInputChange,
    allOffers,
    errors,
    currency,
    defaultOffer,
    products,
    handleMultipleInputChanges,
    activeProduct,
    volume_product_selection,
    isMobile = false,
    setValue,
}: any) => {
    const { sft } = useContext(AppConfigContext);
    const offer = allOffers[selectedTab];
    if (!offer) return null;
    const bundleProducts = offer.bundle_products || [];

    const handleAddProduct = async () => {
        const preSelected = bundleProducts?.map((product: ProductObj) => ({
            id: product?.id,
            variants: product?.variants.map((variant: VariantsObj) => ({ id: variant?.id })),
        }));
        const selected = await window.shopify.resourcePicker({
            type: "product",
            multiple: 5,
            action: "select",
            selectionIds: preSelected,
            filter: { query: "status:active AND published_status:published", variants: false },
        });

        if (selected) {
            try {
                let newProductsArr = await getProductsDynamicallyAndReturnNewProductsArray(
                    selected,
                    sft,
                    false,
                    BundleTypes.VOLUME_DISCOUNT,
                );
                newProductsArr = newProductsArr.map((p: ProductObj) => {
                    const existing = bundleProducts.find((bp: ProductObj) => bp.id === p.id);
                    return {
                        ...p,
                        discountType: existing?.discountType || "percentage",
                        discountValue: existing?.discountValue ?? 0,
                        quantity: existing?.quantity ?? 1,
                        selectedVariants: [],
                    };
                });
                handleInputChange(selectedTab, "bundle_products", [...newProductsArr]);
            } catch {
                console.error("Some error occurred while setting bundle products");
            }
        }
    };

    const handleUpdateProduct = (pIndex: number, field: string, value: any) => {
        const updatedBundle = [...bundleProducts];
        updatedBundle[pIndex] = { ...updatedBundle[pIndex], [field]: value };
        handleInputChange(selectedTab, "bundle_products", updatedBundle);
    };

    const handleDeleteProduct = (pIndex: number) => {
        const updatedBundle = bundleProducts.filter((_: any, i: number) => i !== pIndex);
        handleInputChange(selectedTab, "bundle_products", updatedBundle);
    };

    return (
        <div className={styles.productBundleSetup}>
            <div style={{ marginTop: "16px", marginBottom: "16px" }}>
                <div style={{ marginBottom: "8px", fontWeight: "600", color: "#202223" }}>Choose template</div>
                <div className={styles.templateSelectorContainer}>
                    <div
                        className={`${styles.templateOption} ${offer.bundle_layout === "vertical" ? styles.templateOptionActive : ""}`}
                        onClick={() => handleInputChange(selectedTab, "bundle_layout", "vertical")}
                    >
                        <div className={styles.templateOptionIcon}>
                            <div className={styles.templateIconRow}></div>
                            <div className={styles.templateIconRow}></div>
                            <div className={styles.templateIconRow}></div>
                        </div>
                        <div className={styles.templateOptionLabel}>Vertical</div>
                    </div>
                    <div
                        className={`${styles.templateOption} ${offer.bundle_layout === "horizontal" ? styles.templateOptionActive : ""}`}
                        onClick={() => handleInputChange(selectedTab, "bundle_layout", "horizontal")}
                    >
                        <div className={`${styles.templateOptionIcon} ${styles.horizontalIcon}`}>
                            <div className={styles.templateIconRow}></div>
                            <div className={styles.templateIconRow}></div>
                            <div className={styles.templateIconRow}></div>
                        </div>
                        <div className={styles.templateOptionLabel}>Horizontal</div>
                    </div>
                </div>
            </div>

            <div
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}
            >
                <div style={{ fontWeight: "600", fontSize: "16px" }}>Product Bundle Setup</div>
                <Button onClick={handleAddProduct} icon={PlusIcon}>
                    Add product to bundle
                </Button>
            </div>

            <div className={styles.bundleTable} style={{ ...(isMobile && { zoom: 0.7 }) }}>
                <div className={styles.bundleTableHeader}>
                    <div style={{ flex: 2 }}>PRODUCT</div>
                    <div style={{ flex: 1 }}>QTY</div>
                    <div style={{ flex: 2 }}>DISCOUNT TYPE</div>
                    <div style={{ flex: 1.5 }}>DISCOUNT VALUE</div>
                    <div style={{ width: "40px" }}></div>
                </div>
                <div className={styles.bundleTableRow}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", position: "relative" }}>
                        <div style={{ display: isMobile ? "none" : "block" }}>
                            <Thumbnail
                                source={
                                    ["all", "exclude"].includes(volume_product_selection)
                                        ? ProductIcon
                                        : activeProduct?.images?.[0]?.originalSrc
                                          ? activeProduct?.images?.[0]?.originalSrc
                                          : ProductIcon
                                }
                                alt=""
                                size="small"
                            />
                        </div>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                            {["all", "exclude"].includes(volume_product_selection)
                                ? "Main Product"
                                : (activeProduct?.title ?? "Main Product")}
                        </div>
                        <div style={{ position: "absolute", top: -15, left: -15, transform: "scale(0.7)" }}>
                            <Badge tone="info" size="small">
                                Required
                            </Badge>
                        </div>
                    </div>
                    <div style={{ minWidth: "100%" }}>
                        <FormTextField
                            name={`volume_discounts.${selectedTab}.quantity`}
                            label="Quantity"
                            labelHidden
                            type="number"
                            value={sanitizeValue(offer.quantity)}
                            onChange={(value: string) => {
                                const sanitizedValue = sanitizeValue(parseFloat(value));
                                const finalValue = Math.max(0, Math.min(+sanitizedValue, 2000));
                                handleInputChange(selectedTab, "quantity", finalValue);
                            }}
                            autoComplete="off"
                            min={1}
                        />
                    </div>
                    <div style={{ minWidth: "100%" }}>
                        <FormSelect
                            name={`volume_discounts.${selectedTab}.discount_type`}
                            label="Discount Type"
                            labelHidden
                            options={getDiscountOptionsWIthoutFixed(getCurrencySymbol(currency))}
                            onChange={(value) => handleInputChange(selectedTab, "discount_type", value)}
                            value={offer.discount_type}
                        />
                    </div>
                    <div style={{ minWidth: "100%" }}>
                        <FormTextField
                            name={`volume_discounts.${selectedTab}.discount_value`}
                            label="Discount Value"
                            labelHidden
                            type="number"
                            placeholder="5"
                            value={offer.discount_value?.toString() ?? ""}
                            onChange={(value: string) => {
                                const regex = /^(\d+(\.\d{0,2})?)?$/;
                                if (regex.test(value)) {
                                    if (offer.discount_type === "percentage" && +value > 100) value = "100";
                                    handleInputChange(selectedTab, "discount_value", value);
                                }
                            }}
                            suffix={getDiscountSuffix(offer.discount_type, currency) ?? undefined}
                            autoComplete="off"
                            onBlur={() =>
                                handleInputChange(
                                    selectedTab,
                                    "discount_value",
                                    offer.discount_value?.toString() || "0",
                                )
                            }
                        />
                    </div>
                    <div style={{ width: "20px" }}>
                        <Button icon={DeleteIcon} tone="critical" variant="tertiary" disabled={true} />
                    </div>
                </div>
                {bundleProducts.map((product: ProductObj, pIndex: number) => (
                    <div key={pIndex} className={styles.bundleTableRow}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div style={{ display: isMobile ? "none" : "block" }}>
                                <Thumbnail
                                    source={product?.images?.[0]?.originalSrc || ProductIcon}
                                    alt=""
                                    size="small"
                                />
                            </div>
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "wrap" as any }}>
                                {product.title}
                            </div>
                        </div>
                        <div style={{ minWidth: "100%" }}>
                            <FormTextField
                                name={`volume_discounts.${selectedTab}.bundle_products.${pIndex}.quantity`}
                                label="Quantity"
                                labelHidden
                                type="number"
                                value={product.quantity?.toString() ?? "1"}
                                onChange={(val: string) => handleUpdateProduct(pIndex, "quantity", parseInt(val) || 1)}
                                autoComplete="off"
                                min={1}
                            />
                        </div>
                        <div style={{ minWidth: "100%" }}>
                            <FormSelect
                                name={`volume_discounts.${selectedTab}.bundle_products.${pIndex}.discountType`}
                                label="Discount Type"
                                labelHidden
                                options={getDiscountOptionsWIthoutFixed(getCurrencySymbol(currency))}
                                value={product.discountType}
                                onChange={(val: string) => handleUpdateProduct(pIndex, "discountType", val)}
                            />
                        </div>
                        <div style={{ minWidth: "100%" }}>
                            <FormTextField
                                name={`volume_discounts.${selectedTab}.bundle_products.${pIndex}.discountValue`}
                                label="Discount Value"
                                labelHidden
                                type="number"
                                value={product.discountValue?.toString() ?? ""}
                                onChange={(value: string) => {
                                    const regex = /^(\d+(\.\d{0,2})?)?$/;
                                    if (regex.test(value)) {
                                        if (product.discountType === "percentage" && +value > 100) value = "100";
                                        handleUpdateProduct(pIndex, "discountValue", parseFloat(value) || 0);
                                    }
                                }}
                                suffix={getDiscountSuffix(product.discountType, currency) ?? undefined}
                                autoComplete="off"
                            />
                        </div>
                        <div style={{ width: "20px" }}>
                            <Button
                                icon={DeleteIcon}
                                tone="critical"
                                variant="tertiary"
                                onClick={() => handleDeleteProduct(pIndex)}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div style={{ marginBlock: "16px" }}>
                <Divider />
            </div>

            <div className={styles.inputGroup}>
                <OfferTitleField offer={offer} index={selectedTab} handleInputChange={handleInputChange} />
                <OfferTagField offer={offer} index={selectedTab} handleInputChange={handleInputChange} />
                <OfferLabelField offer={offer} index={selectedTab} handleInputChange={handleInputChange} />
                <DiscountLabelBadgePicker offer={offer} index={selectedTab} handleInputChange={handleInputChange} />
                <SelectedByDefaultField index={selectedTab} defaultOffer={defaultOffer} setValue={setValue} />
            </div>
            <OfferActionButtons offer={offer} index={selectedTab} handleInputChange={handleInputChange} />
            <OfferEditors
                offer={offer}
                index={selectedTab}
                handleInputChange={handleInputChange}
                errors={errors}
                products={products}
                handleMultipleInputChanges={handleMultipleInputChanges}
                currency={currency}
            />
        </div>
    );
};

// export const AllowImageVolume = ({ handleInputChange, selectedTab, img_url, img_size, img_radius, img_fit }: any) => (
//     <div
//         style={{
//             backgroundColor: "#f9f9f9",
//             padding: "16px",
//             marginTop: "16px",
//             borderRadius: "8px",
//             display: "flex",
//             flexDirection: "column",
//             gap: "10px",
//             position: "relative",
//         }}
//     >
//         <span
//             style={{ position: "absolute", right: "10px", top: "10px", cursor: "pointer" }}
//             onClick={() => handleInputChange(selectedTab, "allow_img", false)}
//         >
//             <Icon source={DeleteIcon as IconSource} tone="critical" accessibilityLabel="Delete" />
//         </span>

//         <ImageUploader
//             handleInputChange={handleInputChange}
//             selectedTab={selectedTab}
//             img_url={img_url}
//             keyName="img_url"
//         />

//         <FormSelect
//             name={`volume_discounts.${selectedTab}.img_fit`}
//             label="Image Fit"
//             options={[
//                 { label: "Cover", value: "cover" },
//                 { label: "Contain", value: "contain" },
//                 { label: "Fill", value: "fill" },
//             ]}
//             value={img_fit || "cover"}
//             onChange={(value) => handleInputChange(selectedTab, "img_fit", value)}
//         />

//         <FormRangeSlider
//             name={`volume_discounts.${selectedTab}.img_size`}
//             min={30}
//             max={100}
//             label="Image Size"
//             value={img_size}
//             onChange={(val: number) => handleInputChange(selectedTab, "img_size", +val)}
//             suffixTemplate={(v) => `${v || 0}px`}
//             wrapperClassName={commonFieldsStyles.rangeSliderOverride}
//         />
//         <FormRangeSlider
//             name={`volume_discounts.${selectedTab}.img_radius`}
//             min={0}
//             max={100}
//             label="Image Border Radius"
//             value={img_radius}
//             onChange={(val: number) => handleInputChange(selectedTab, "img_radius", +val)}
//             suffixTemplate={(v) => `${v || 0}px`}
//             wrapperClassName={commonFieldsStyles.rangeSliderOverride}
//         />
//     </div>
// );
